import React, { FC, useRef } from 'react'
import {
  convertDefaultToLocaleCurrency,
  formatNumberToCurrencyCommas,
  getCurrencySymbol,
} from 'components/FormatPrice/helpers'
import { updateClientSideUrl, getHrefFunction } from '../../helpers/url'
import CheckboxListFacet from '../../components/CheckboxListFacet'
import CustomPriceFields, { CustomPriceFieldsApi } from './CustomPriceFields'
import { useSelector } from 'react-redux'
import { getUserPreferencesSelectedCurrency } from 'selectors/user/cookie'
import {
  CustomHandleUpdateSelectedFacetParams,
  FacetServiceApi,
  UseReduxSearchProviderResponse,
} from '../../types'
import useReduxSearchProvider from '../../hooks/useReduxSearchProvider'

const PRICE_RANGES = [
  {
    label: 'Under $500',
    presetMin: 0,
    presetMax: 500,
    format: 'under',
  },
  {
    label: '$500 - $1,000',
    presetMin: 500,
    presetMax: 1000,
    format: 'divider',
  },
  {
    label: '$1,000 - $2,000',
    presetMin: 1000,
    presetMax: 2000,
    format: 'divider',
  },
  {
    label: '$2,000 - $5,000',
    presetMin: 2000,
    presetMax: 5000,
    format: 'divider',
  },
  {
    label: '$5,000 - $10,000',
    presetMin: 5000,
    presetMax: 10000,
    format: 'divider',
  },
  {
    label: 'Over $10,000',
    presetMin: 10000,
    presetMax: 500000,
    format: 'over',
  },
]

type PriceAttributeObject = {
  label: string
  presetMin: number
  presetMax: number
  format: string
}

type MultiplePriceFacetProps = {
  baseUrl: string
  nextAppRoute: string
  facetService: FacetServiceApi
  onFilterCallback?: (hrefParam: string) => void
  facetName: string
  attributesList?: ReadonlyArray<PriceAttributeObject>
  isCustomPriceEnabled?: boolean
  useReduxProviderHook?: () => UseReduxSearchProviderResponse
  customHandleUpdateSelectedFacet?: (params: CustomHandleUpdateSelectedFacetParams) => void
  customClickFacetOption?: (params: CustomHandleUpdateSelectedFacetParams) => void
}

const MultiplePriceFacet: FC<MultiplePriceFacetProps> = ({
  baseUrl,
  nextAppRoute,
  facetService,
  onFilterCallback,
  facetName,
  attributesList = PRICE_RANGES,
  isCustomPriceEnabled,
  useReduxProviderHook = useReduxSearchProvider,
  customHandleUpdateSelectedFacet,
  customClickFacetOption,
}) => {
  const {
    selectedFacets,
    basicQueryParams,
    pathParams,
    sharedFacetNames,
    pageParams,
    priceMinMax,
  } = useReduxProviderHook()
  const selectedCurrency = useSelector(getUserPreferencesSelectedCurrency)

  const customPriceFieldsRef = useRef<CustomPriceFieldsApi>(null)

  const createLabel = ({ presetMin, presetMax, format, selectedCurrency }): string => {
    const currencySymbol = getCurrencySymbol(selectedCurrency)

    switch (format) {
      case 'under':
        const convertedUnderPrice = convertDefaultToLocaleCurrency(presetMax, selectedCurrency)
        return `Under ${currencySymbol}${formatNumberToCurrencyCommas(
          convertedUnderPrice,
          selectedCurrency.locale
        )}`

      case 'over':
        const convertedOverPrice = convertDefaultToLocaleCurrency(presetMin, selectedCurrency)
        return `Over ${currencySymbol}${formatNumberToCurrencyCommas(
          convertedOverPrice,
          selectedCurrency.locale
        )}`

      case 'divider':
      default:
        const convertedMinPrice = convertDefaultToLocaleCurrency(presetMin, selectedCurrency)
        const convertedMaxPrice = convertDefaultToLocaleCurrency(presetMax, selectedCurrency)
        return `${currencySymbol}${formatNumberToCurrencyCommas(
          convertedMinPrice,
          selectedCurrency.locale
        )} - ${currencySymbol}${formatNumberToCurrencyCommas(
          convertedMaxPrice,
          selectedCurrency.locale
        )}`
    }
  }

  const handleUpdateSelectedFacets = ({
    newSelectedFacets,
    newQueryParams,
    facetName,
    facetValue,
  }) => {
    const newSelectedFacetsAndQueryParams = {
      ...newSelectedFacets,
      ...newQueryParams,
    }
    const href = getHrefFunction({
      facetsAndQueryParams: newSelectedFacetsAndQueryParams,
      pathParams,
      sharedFacetNames,
    })()
    const newUrl = `${baseUrl}${href}`

    if (Boolean(customHandleUpdateSelectedFacet)) {
      customHandleUpdateSelectedFacet?.({
        newSelectedFacets: newSelectedFacetsAndQueryParams,
        newUrl,
        facetName,
        facetValue,
      })
      customClickFacetOption?.({
        newSelectedFacets: newSelectedFacetsAndQueryParams,
        newUrl,
        facetName,
        facetValue,
      })
      onFilterCallback?.(newUrl)
      return
    }

    customClickFacetOption?.({
      newSelectedFacets: newSelectedFacetsAndQueryParams,
      newUrl,
      facetName,
      facetValue,
    })
    updateClientSideUrl(newUrl, nextAppRoute)
    onFilterCallback?.(newUrl)
  }

  const defaultPriceValues = attributesList.map(
    ({ presetMin, presetMax }) => `${presetMin}-${presetMax}`
  )

  const filterOptions = attributesList.map(({ presetMin, presetMax, format }) => {
    const multiplePriceFacetValue = `${presetMin}-${presetMax}`
    const href = getHrefFunction({
      facetsAndQueryParams: { [facetName]: [multiplePriceFacetValue], page: [''] },
      pathParams,
      sharedFacetNames,
    })()
    const newHref = `${baseUrl}${href}`

    return {
      text: createLabel({
        presetMin,
        presetMax,
        format,
        selectedCurrency,
      }),
      checked: facetService.getIsFacetSelected({
        selectedFacets,
        pageParams,
        facetValue: multiplePriceFacetValue,
        facetName,
      }),
      onClick: () => {
        facetService.handleClickFacet({
          facetValue: multiplePriceFacetValue,
          facetName,
          selectedFacets,
          basicQueryParams,
          updateSelectedFacetsAndQueryParams: handleUpdateSelectedFacets,
        })
      },
      href: newHref,
    }
  })

  return (
    <CheckboxListFacet
      facetName={facetName}
      facetService={facetService}
      nextAppRoute={nextAppRoute}
      baseUrl={baseUrl}
      facetOptions={[]}
      customFilterOptions={filterOptions}
      useReduxProviderHook={useReduxProviderHook}
      customHandleUpdateSelectedFacet={customHandleUpdateSelectedFacet}
      customClickFacetOption={customClickFacetOption}
    >
      {isCustomPriceEnabled && (
        <CustomPriceFields
          ref={customPriceFieldsRef}
          facetName={facetName}
          baseUrl={baseUrl}
          nextAppRoute={nextAppRoute}
          priceMinMax={priceMinMax}
          selectedCurrency={selectedCurrency}
          selectedFacets={selectedFacets}
          basicQueryParams={basicQueryParams}
          defaultPriceValues={defaultPriceValues}
          customHandleUpdateSelectedFacet={customHandleUpdateSelectedFacet}
          customClickFacetOption={customClickFacetOption}
          sharedFacetNames={sharedFacetNames}
          pathParams={pathParams}
        />
      )}
    </CheckboxListFacet>
  )
}

export default MultiplePriceFacet
